import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { FiMail, FiMapPin, FiPhoneCall, FiSend } from "react-icons/fi";

import PageHero from "../../components/PageHero/PageHero";
import Reveal from "../../components/Reveal/Reveal";
import SEO from "../../components/SEO/SEO";
import { companyInfo, pageImages } from "../../data/siteContent";

export default function ContactUs() {
  const formRef = useRef(null);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await emailjs.sendForm(
        "service_1heep8r",
        "template_04a9pol",
        formRef.current,
        "1o9Czo0CSjpMNedaN"
      );

      formRef.current.reset();
      setStatus({
        type: "success",
        message: "Your message has been sent. We will get back to you soon.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: "We could not send the message right now. Please try again in a moment.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappHref = `https://wa.me/${companyInfo.whatsappNumber}?text=${encodeURIComponent(
    companyInfo.whatsappMessage
  )}`;

  return (
    <>
      <SEO
        title="Contact"
        description="Contact Thamizhi International Ventures for buyer discussions, product inquiries, sourcing requirements, and export planning support."
        path="/contact"
        image={pageImages.contact}
      />

      <PageHero
        eyebrow="Contact us"
        title="Contact Us"
        description="Reach out for product discussions, sourcing requests, packaging questions, or export coordination. We are available by phone, email, WhatsApp, and the contact form below."
        actions={[
          { label: "WhatsApp us", href: whatsappHref, external: true, variant: "primary" },
          {
            label: "Email us",
            href: `mailto:${companyInfo.email}`,
            variant: "secondary",
          },
        ]}
        stats={[
          {
            label: "Phone",
            value: (
              <>
                <span>{companyInfo.primaryPhone}</span>
                <span>{companyInfo.secondaryPhone}</span>
              </>
            ),
          },
          { label: "Email", value: companyInfo.email },
          { label: "Office", value: companyInfo.location },
        ]}
        media={
          <div className="overlap-visual overlap-visual--tall">
            <div className="overlap-visual__backdrop" aria-hidden="true" />
            <img src={pageImages.contact} alt="Contact Thamizhi International Ventures" />
          </div>
        }
      />

      <section className="section-band">
        <div className="section-shell contact-layout">
          <Reveal className="contact-layout__info">
            <div className="section-heading">
              <p className="section-kicker">Direct channels</p>
              <h2 className="section-title">Talk to the team through the channel that fits your workflow</h2>
            </div>

            <div className="contact-card-list">
              <article className="contact-card">
                <div className="contact-card__icon">
                  <FiPhoneCall aria-hidden="true" />
                </div>
                <div className="contact-card__body">
                  <h3>Phone</h3>
                  <a href={companyInfo.phoneHref}>{companyInfo.primaryPhone}</a>
                  <a href="tel:+919445984937">{companyInfo.secondaryPhone}</a>
                </div>
              </article>

              <article className="contact-card">
                <div className="contact-card__icon">
                  <FiMail aria-hidden="true" />
                </div>
                <div className="contact-card__body">
                  <h3>Email</h3>
                  <a href={`mailto:${companyInfo.email}`}>{companyInfo.email}</a>
                </div>
              </article>

              <article className="contact-card">
                <div className="contact-card__icon">
                  <FiMapPin aria-hidden="true" />
                </div>
                <div className="contact-card__body">
                  <h3>Office</h3>
                  {companyInfo.addressLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </article>
            </div>
          </Reveal>

          <Reveal className="contact-form-panel" delay={120}>
            <form ref={formRef} className="contact-form" onSubmit={sendEmail}>
              <div className="section-heading">
                <p className="section-kicker">Send a message</p>
                <h2 className="section-title">Share your requirement</h2>
              </div>

              <label className="form-field">
                <span>Your name</span>
                <input type="text" name="user_name" placeholder="Enter your name" required />
              </label>

              <label className="form-field">
                <span>Email address</span>
                <input type="email" name="user_email" placeholder="Enter your email" required />
              </label>

              <label className="form-field">
                <span>Subject</span>
                <input type="text" name="user_subject" placeholder="What would you like to discuss?" required />
              </label>

              <label className="form-field">
                <span>Message</span>
                <textarea
                  name="user_message"
                  rows="6"
                  placeholder="Tell us about the product range, quantity, market, or support you need."
                  required
                />
              </label>

              <button type="submit" className="button button-primary form-submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send message"}
                <FiSend aria-hidden="true" />
              </button>

              {status.message ? (
                <p
                  className={`form-status${status.type === "error" ? " is-error" : " is-success"}`}
                  aria-live="polite"
                >
                  {status.message}
                </p>
              ) : null}
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
